import { Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import * as bd from 'react-basic-design';
import { LicenseDTO, useUpdateLicenseKeyApi } from '../../../api/license-api';
import { ProductDTO } from '../../../api/product-api';
import SvgClose from '../../../assets/icons/Close';
import SvgDelete from '../../../assets/icons/Delete';
import SvgEdit from '../../../assets/icons/Edit';
import SvgSave from '../../../assets/icons/Save';
import { BasicModal } from '../../../components/basic/basic-modal';
import { calendar } from '../../../components/basic/calendar';
import { notify } from '../../../components/basic/notify';
import { T } from '../../../components/basic/text';
import { yup } from '../../../components/basic/yup';
import { FormikTextField } from '../../../components/formik';
import { LoadingButton } from '../../../components/loading-button';

interface SaveLicenseModalProps {
    show: boolean;
    allowEdit: boolean;
    selectedLicense?: LicenseDTO;
    products: ProductDTO[];
    onHide: () => void;
    onSave: (values: LicenseDTO) => void;
    onDelete?: (license: LicenseDTO) => void;
    onKeyUpdated?: (license: LicenseDTO) => void;
}

export function SaveLicenseModal({
    show,
    allowEdit,
    selectedLicense,
    products,
    onHide,
    onSave,
    onDelete,
    onKeyUpdated,
}: SaveLicenseModalProps) {
    const [isEditingKey, setIsEditingKey] = useState(false);
    const [newLicenseKey, setNewLicenseKey] = useState('');
    const updateLicenseKeyApi = useUpdateLicenseKeyApi();

    // لایسنس استفاده شده است اگر licenseCode پر باشد
    const isUsed = !!selectedLicense?.licenseCode;

    // Reset state when modal opens/closes or license changes
    useEffect(() => {
        setIsEditingKey(false);
        setNewLicenseKey('');
    }, [show, selectedLicense?.serial]);

    const handleUpdateKey = () => {
        if (!selectedLicense?.serial || !newLicenseKey.trim()) return;

        updateLicenseKeyApi.post(
            null,
            { serial: selectedLicense.serial, newLicenseKey: newLicenseKey.trim() },
            res => {
                notify.success('کلید لایسنس با موفقیت تغییر کرد');
                setIsEditingKey(false);
                setNewLicenseKey('');
                if (onKeyUpdated) {
                    onKeyUpdated(res.license);
                }
            },
            notify.error
        );
    };

    const LicenseSchema = yup.object().shape({
        productId: yup.number().notOneOf([0], ' ').required(' '),
        licenseKey: yup.string().required(' '),
    });

    const defaultValues: LicenseDTO = {
        serial: selectedLicense?.serial,

        productId: selectedLicense ? selectedLicense.productId : 0,
        licenseKey: selectedLicense ? selectedLicense.licenseKey : '',
        isDelivered: selectedLicense ? selectedLicense.isDelivered : false,
        deliveredAt: selectedLicense ? selectedLicense.deliveredAt : undefined,
        deliveredToCodeId: selectedLicense ? selectedLicense.deliveredToCodeId : 0,
    };

    if (!show) return <></>;

    return (
        <Formik initialValues={defaultValues} validationSchema={LicenseSchema} onSubmit={onSave}>
            {formik => (
                <BasicModal
                    show={show}
                    onHide={onHide}
                    size="xl"
                    title={
                        <bd.Flex className="w-100" align="center" content="between">
                            <bd.Flex align="center" content="start">
                                <T>{selectedLicense ? 'edit-license' : 'new-license'}</T>
                                {defaultValues.isDelivered && (
                                    <>
                                        <span className="px-2">|</span>
                                        <T
                                            className="small text-muted"
                                            date={calendar.dateToString(defaultValues.deliveredAt)}
                                        >
                                            this-license-is-delivered-@date
                                        </T>
                                    </>
                                )}
                            </bd.Flex>
                            <bd.Flex align="center" gap={2}>
                                <bd.Button
                                    variant="icon"
                                    size="sm"
                                    onClick={formik.submitForm}
                                    disabled={!allowEdit}
                                >
                                    <SvgSave />
                                </bd.Button>
                                {selectedLicense && onDelete && (
                                    <bd.Button
                                        disabled={!allowEdit}
                                        variant="icon"
                                        color="secondary"
                                        size="sm"
                                        onClick={() => onDelete(selectedLicense)}
                                    >
                                        <SvgDelete />
                                    </bd.Button>
                                )}
                                <bd.Button
                                    variant="icon"
                                    color="secondary"
                                    size="sm"
                                    onClick={onHide}
                                >
                                    <SvgClose />
                                </bd.Button>
                            </bd.Flex>
                        </bd.Flex>
                    }
                >
                    <Form>
                        <bd.Flex align="center" wrap>
                            <FormikTextField
                                label="product-id"
                                name="productId"
                                readOnly={!allowEdit}
                                type="select"
                                items={[
                                    { id: null, title: '' },
                                    ...products.map(product => ({
                                        id: product.id,
                                        title: `${product.name} `,
                                    })),
                                ]}
                                width="11rem"
                                handleEnter
                            />

                            <FormikTextField
                                label="license-key"
                                name="licenseKey"
                                readOnly={!allowEdit}
                                className="flex-grow-1"
                                handleEnter
                            />
                        </bd.Flex>

                        {/* نمایش license-code اگر لایسنس استفاده شده باشد */}
                        {isUsed && (
                            <bd.Flex align="center" className="mt-3">
                                <div className="flex-grow-1">
                                    <label className="form-label text-muted small">
                                        <T>license-code</T>
                                    </label>
                                    <div className="badge bg-success fs-6 d-block text-start p-2">
                                        {selectedLicense?.licenseCode}
                                    </div>
                                </div>
                            </bd.Flex>
                        )}

                        {/* بخش ویرایش کلید لایسنس وقتی استفاده شده است */}
                        {isUsed && (
                            <bd.Paper className="mt-3 p-3 bg-warning-subtle border border-warning">
                                <bd.Flex align="center" content="between">
                                    <span className="text-warning fw-bold">
                                        <T>این لایسنس استفاده شده است. می‌توانید کلید لایسنس را تغییر دهید.</T>
                                    </span>
                                    {!isEditingKey && (
                                        <bd.Button
                                            variant="contained"
                                            className="btn-warning"
                                            size="sm"
                                            onClick={() => {
                                                setIsEditingKey(true);
                                                setNewLicenseKey(selectedLicense?.licenseKey || '');
                                            }}
                                        >
                                            <SvgEdit />
                                            <T>edit-license-key</T>
                                        </bd.Button>
                                    )}
                                </bd.Flex>

                                {isEditingKey && (
                                    <bd.Flex align="end" gap={2} className="mt-3">
                                        <div className="flex-grow-1">
                                            <label className="form-label">
                                                <T>new-license-key</T>
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={newLicenseKey}
                                                onChange={e => setNewLicenseKey(e.target.value)}
                                                placeholder="کلید لایسنس جدید را وارد کنید..."
                                            />
                                        </div>
                                        <LoadingButton
                                            variant="contained"
                                            className="btn-success"
                                            size="sm"
                                            icon={<SvgSave />}
                                            label={<T>save</T>}
                                            onClick={handleUpdateKey}
                                            loading={updateLicenseKeyApi.busy}
                                            disabled={!newLicenseKey.trim()}
                                        />
                                        <bd.Button
                                            variant="outline"
                                            color="secondary"
                                            size="sm"
                                            onClick={() => {
                                                setIsEditingKey(false);
                                                setNewLicenseKey('');
                                            }}
                                        >
                                            <T>cancel</T>
                                        </bd.Button>
                                    </bd.Flex>
                                )}
                            </bd.Paper>
                        )}
                    </Form>
                </BasicModal>
            )}
        </Formik>
    );
}
